<script lang="ts">
    import type {PagedParameteredResult} from 'typings/portaro.be.types';
    import {getInjector, getLocalization} from 'core/svelte-context/context';
    import {SearchPageService} from './search-page.service';
    import KpSearchHeader from '../kp-search-header/KpSearchHeader.svelte';
    import KpSearchResultInfo from '../kp-search-result-info/KpSearchResultInfo.svelte';
    import KpFavouritesPanel from '../../favourites/kp-favourites-panel/KpFavouritesPanel.svelte';
    import KpVisitedPanel from '../../record/kp-visited-documents-panel/KpVisitedPanel.svelte';
    import KpSearchAlphabets from '../kp-search-alphabets/KpSearchAlphabets.svelte';
    import type {SearchManager} from '../search-manager/search-manager';
    import {onDestroy, onMount} from 'svelte';
    import type {Subscription} from 'rxjs';
    import {unsubscribeAllSubscriptions} from 'shared/utils/observables-utils';
    import type {RecordCollectionUpdateType} from '../../record-collections/record-collection-manager';
    import KpSearchFooter from '../kp-search-footer/KpSearchFooter.svelte';
    import {exists} from 'shared/utils/custom-utils';
    import KpSearchSideContent from '../kp-search-content-layouts/KpSearchSideContent.svelte';
    import KpSearchMainContent from '../kp-search-content-layouts/KpSearchMainContent.svelte';
    import {isDocument} from '../kp-resolvable-search-results-displayer/search-result-resolver';
    import KpButton from 'shared/ui-widgets/button/KpButton.svelte';
    import KpSearchResultsExportButton from '../kp-search-footer/KpSearchResultsExportButton.svelte';

    export let searchManager: SearchManager<any>;

    const localize = getLocalization();
    const service = getInjector().getByClass(SearchPageService);

    $: lastSearch = searchManager.getLastSearch$();

    let favouritesInsertsSubscription: Subscription;
    let searchesSubscription: Subscription;
    let asideElement: HTMLElement;

    onMount(async () => {

        searchesSubscription = searchManager.getState$().subscribe((state) => {
            if (exists(state.pageLoadInProgress)) {
                service.clearPotentials();
            }
        })

        const favouritesCollectionManager = await service.getFavouritesCollectionManager();
        favouritesInsertsSubscription = favouritesCollectionManager
            .getCollectionUpdateEvent$()
            .subscribe((event) => scrollToFavouritesPanelAfterFirstInsert(event.type, favouritesCollectionManager.getSize()))
    });

    $: handleLastSearchResults($lastSearch?.result);

    onDestroy(() => {
        service.clearPotentials();
        unsubscribeAllSubscriptions(favouritesInsertsSubscription, searchesSubscription);
    });

    function scrollToFavouritesPanelAfterFirstInsert(type: RecordCollectionUpdateType, size: number) {
        if (type === 'insert' && size === 1) {
            asideElement?.querySelector('#favourites-panel')?.scrollIntoView({ behavior: 'smooth', block: 'center'});
        }
    }

    function handleLastSearchResults(searchResult: PagedParameteredResult<any, any> | undefined) {
        if (exists(searchResult)) {
            service.registerPotentials(searchResult.content.filter(isDocument));
        }
    }
</script>

<div class="container search type-{$lastSearch?.type ?? ''}">

    <KpSearchHeader {searchManager}/>

    <KpSearchAlphabets {searchManager}/>

    <KpSearchResultInfo {searchManager}/>

    <div class="row">
        <div class="main-panel-hlavni col-sm-8">
            <KpSearchMainContent {searchManager}/>
        </div>

        <aside class="main-panel-pravy col-sm-4" bind:this={asideElement}>
            <KpSearchSideContent {searchManager}/>

            <KpFavouritesPanel/>

            <KpVisitedPanel/>
        </aside>
    </div>

    <KpSearchFooter {searchManager}>
        {#if $lastSearch.availableExports.length > 0}
            <KpSearchResultsExportButton availableExports={$lastSearch.availableExports}/>
            <span role="separator" aria-hidden="true">|</span>
        {/if}
        <KpButton buttonSize="xs" additionalClasses="add-sdi-request-button" on:click={() => service.addSdiRequest($lastSearch)}>
            {localize(/* @kp-localization sdi.PosilatNovinkyZTohotoHledani */ 'sdi.PosilatNovinkyZTohotoHledani')}
        </KpButton>
        <span role="separator" aria-hidden="true">|</span>
        <a href="/search-history">{localize(/* @kp-localization hledani.ZobrazitHledaneDotazy */ 'hledani.ZobrazitHledaneDotazy')}</a>
    </KpSearchFooter>
</div>

<style lang="less">
    .search {
        margin-top: 20px;
    }
</style>